import { useEffect, useState } from 'react';
import { money, nextPayoutPreview, type NextPayoutPreview } from '@kilat/shared';

import { useI18n } from '../lib/use-i18n.js';
import { logger } from '../lib/logger.js';
import { agentsRepo } from '../lib/repositories/earnings-repo.js';

/**
 * When the agent gets paid, and whether they will.
 *
 * `/earnings` showed an accrued balance and stopped there. An agent looking at
 * "RM 84.00 accrued" has two questions that screen never answered — when does
 * this arrive, and why has it not already — and the second is the one that
 * costs sales. An agent who concludes they have been shorted stops selling, and
 * nothing on the screen told them they were simply RM 16 short of the minimum.
 *
 * Both facts were already in the data: `thresholdSen` on the agent's `payout`
 * details, and the payout day in the platform commission rules. Nothing joined
 * them up. The arithmetic lives in `nextPayoutPreview` so this component only
 * decides what to say.
 */

/** Payout day fallback, matching `defaultCommissionRules.payoutDayOfMonth`. */
const FALLBACK_PAYOUT_DAY = 7;

interface Props {
  readonly agentId: string;
  /** Unpaid commission, in sen — the same figure `/earnings` shows. */
  readonly accruedSen: number;
}

export function NextPayoutCard({ agentId, accruedSen }: Props) {
  const t = useI18n();
  const [preview, setPreview] = useState<NextPayoutPreview | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const profile = await agentsRepo.get(agentId);
        if (cancelled) return;
        setPreview(
          nextPayoutPreview({
            accruedSen,
            thresholdSen: profile?.payout?.thresholdSen ?? 10_000,
            payoutDayOfMonth: FALLBACK_PAYOUT_DAY,
            nowMs: Date.now(),
          }),
        );
      } catch (e) {
        // A profile read failing must not blank the payouts screen. Falling back
        // to the shipped default threshold still answers both questions, and a
        // slightly wrong threshold is far better than an unexplained balance.
        logger.warn('payout preview falling back to defaults', { agentId, error: String(e) });
        if (!cancelled) {
          setPreview(
            nextPayoutPreview({
              accruedSen,
              thresholdSen: 10_000,
              payoutDayOfMonth: FALLBACK_PAYOUT_DAY,
              nowMs: Date.now(),
            }),
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentId, accruedSen]);

  if (!preview) return null;

  const dateLabel = new Intl.DateTimeFormat(t.locale, {
    day: 'numeric',
    month: 'long',
  }).format(new Date(preview.nextRunMs));

  return (
    <section
      style={{
        padding: 'var(--space-5)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--color-border)',
        background: 'var(--color-surface)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
        {t.t('agent.payouts.next.title')}
      </h2>

      {preview.reachesThreshold ? (
        <p style={bodyStyle}>
          {t.t('agent.payouts.next.onTrack', {
            amount: money.format(preview.accruedSen),
            date: dateLabel,
          })}
        </p>
      ) : (
        <>
          {/*
            The shortfall, not the threshold. "You are RM 15.50 away" is one more
            signup; "the minimum is RM 100" is a rule the agent has to do
            arithmetic against while feeling unpaid.
          */}
          <p style={bodyStyle}>
            {t.t('agent.payouts.next.shortfall', {
              shortfall: money.format(preview.shortfallSen),
              threshold: money.format(preview.thresholdSen),
            })}
          </p>
          <p style={{ ...bodyStyle, color: 'var(--color-on-surface-muted)' }}>
            {t.t('agent.payouts.next.thenPaid', { date: dateLabel })}
          </p>
        </>
      )}
    </section>
  );
}

const bodyStyle = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  lineHeight: 1.6,
  color: 'var(--color-on-surface)',
} as const;
